'use client';

import { Effect, Result } from 'effect';
import { useCallback, useState } from 'react';

import { listTaxonTree } from '../effect/bind/taxon';
import { RuntimeClient } from '@vs-shop/engine/runtime/RuntimeClient';
import type { TaxonMenu } from '../effect/schema/openapi/backend';

type TaxonTreeCache = Map<string, readonly TaxonMenu[]>;

type UseTaxonTreeLazyLoadResult = {
  fetchChildren: (taxonCode: string) => Promise<readonly TaxonMenu[]>;
  isLoading: (taxonCode: string) => boolean;
};

/**
 * Hook to fetch taxon children on demand
 * Caches results to avoid redundant API calls
 */
export function useTaxonTreeLazyLoad(): UseTaxonTreeLazyLoadResult {
  const [cache, setCache] = useState<TaxonTreeCache>(new Map());
  const [loadingSet, setLoadingSet] = useState<Set<string>>(new Set());

  const isLoading = useCallback((taxonCode: string) => loadingSet.has(taxonCode), [loadingSet]);

  const fetchChildren = useCallback(
    async (taxonCode: string): Promise<readonly TaxonMenu[]> => {
      // Return from cache if available
      const cached = cache.get(taxonCode);
      if (cached !== undefined) {
        return cached;
      }

      // Skip if already loading (duplicate click protection)
      if (loadingSet.has(taxonCode)) {
        return [];
      }

      setLoadingSet((prev) => new Set(prev).add(taxonCode));

      try {
        const result = await RuntimeClient.runPromise(
          listTaxonTree({
            root_taxon_code: taxonCode,
            depth: 1,
          }).pipe(Effect.result)
        );

        const children = Result.isSuccess(result) ? result.success['hydra:member'] : [];

        setCache((prev) => new Map(prev).set(taxonCode, children));
        setLoadingSet((prev) => {
          const next = new Set(prev);
          next.delete(taxonCode);
          return next;
        });

        return children;
      } catch {
        setLoadingSet((prev) => {
          const next = new Set(prev);
          next.delete(taxonCode);
          return next;
        });
        return [];
      }
    },
    [cache, loadingSet]
  );

  return {
    fetchChildren,
    isLoading,
  };
}
